/// <reference types="office-js" />
import type {
  ActiveDocumentRef,
  ContentFormat,
  HostAdapter,
  Scope,
  SelectionInfo,
} from './host-adapter'
import { markdownToHtml } from './markdown'

/** Word implementation of the HostAdapter (Word.run / Office.js). */
export class WordAdapter implements HostAdapter {
  readonly host = 'word' as const
  readonly canEdit = true

  async getSelection(): Promise<SelectionInfo> {
    return Word.run(async (ctx) => {
      const sel = ctx.document.getSelection()
      sel.load('text')
      await ctx.sync()
      const text = sel.text ?? ''
      const paragraphs = Math.max(1, text.split(/\r?\n/).filter((l) => l.trim().length > 0).length)
      return { text, paragraphs, isEmpty: text.trim().length === 0 }
    })
  }

  async getDocumentText(): Promise<string> {
    return Word.run(async (ctx) => {
      const body = ctx.document.body
      body.load('text')
      await ctx.sync()
      return body.text
    })
  }

  getContent(scope: Scope): Promise<string> {
    if (scope === 'none') return Promise.resolve('')
    return scope === 'selection' ? this.getSelection().then((s) => s.text) : this.getDocumentText()
  }

  async replaceSelection(content: string, format: ContentFormat = 'text'): Promise<void> {
    await Word.run(async (ctx) => {
      const sel = ctx.document.getSelection()
      if (format === 'text') {
        sel.insertText(content, Word.InsertLocation.replace)
      } else {
        sel.insertHtml(toHtml(content, format), Word.InsertLocation.replace)
      }
      await ctx.sync()
    })
  }

  async insertAtCursor(content: string, format: ContentFormat = 'text'): Promise<void> {
    await Word.run(async (ctx) => {
      const sel = ctx.document.getSelection()
      if (format === 'text') {
        sel.insertText(content, Word.InsertLocation.end)
      } else {
        sel.insertHtml(toHtml(content, format), Word.InsertLocation.end)
      }
      await ctx.sync()
    })
  }

  async getActiveDocument(): Promise<ActiveDocumentRef | null> {
    try {
      const url = Office.context?.document?.url ?? ''
      const name = url.split(/[\\/]/).pop() || 'Dokument'
      return { name }
    } catch {
      return null
    }
  }
}

function toHtml(content: string, format: ContentFormat): string {
  return format === 'markdown' ? markdownToHtml(content) : content
}
